import { useMemo } from "react";
import { DivisionModel } from "../../models/division.model";
import { I18nService } from "../i18n/i18n-service";
import { DivisionTranslationEntity } from "@citychallenge/api-public";

/**
 * Hook that returns a DivisionModel with merged translation values based on the active priority language.
 * If the i18nService has a priorityLanguage active, it merges the translation values for that language
 * with the base division values, returning a new division object with translated content.
 *
 * @param activeDivision - The base division model to apply translations to
 * @param i18nService - The internationalization service to check for priority language
 * @returns A new DivisionModel with merged translation values or the original if no priority language
 */
export function useDivisionValues(
  activeDivision: DivisionModel | undefined,
  i18nService: I18nService
): DivisionModel | undefined {
  return useMemo(() => {
    if (!activeDivision) {
      return undefined;
    }

    const priorityLanguage = i18nService.priorityLanguage;

    if (!priorityLanguage || !activeDivision.translations) {
      return activeDivision;
    }

    // Find the translation for the priority language
    const translation = activeDivision.translations.find(
      (t: DivisionTranslationEntity) => t.language?.id === priorityLanguage
    );

    if (!translation) {
      return activeDivision;
    }

    // Create a new division object with merged translation values
    const mergedDivision = new DivisionModel({
      ...activeDivision,
      // Override with translation values where they exist
      ...translation,
    });

    return mergedDivision;
  }, [activeDivision, i18nService.priorityLanguage]);
}
